import type {
  Family,
  FamilyLink,
  FamilyLinkSource,
  FamilyRevRows,
  FamilySignal,
  FamilySummary,
} from "../api/types";
import { parseSeriesSlug } from "./series";
import { plural } from "./format";
import { zTone, zWord } from "./zscore";

/**
 * Разбор консенсуса семьи кошельков для экрана.
 *
 * # Что заменяет эта подсистема
 *
 * Копирование по ДОХОДНОСТИ кошелька измерено и провалено: шесть сценариев,
 * 1730 сведённых сигналов, кромки нет ни в одном (перенос ROI с недели на
 * неделю r = 0.11 — отбор каждый раз даёт новый список случайных счастливчиков,
 * см. lib/signals.ts). Здесь меняются ОБА конца затеи: копируется не кошелёк, а
 * СЕМЬЯ — группа адресов одного оператора, найденная по совпадению СЕКУНДЫ
 * входа, — и отбирается она ОСТАТКОМ НАД ФИЗИКОЙ (перенос r = 0.62, вшестеро
 * надёжнее), а не прибылью.
 *
 * Вне выборки (пул зафиксирован на первой половине недели, суд — на второй)
 * правило «консенсус ОДНОЙ семьи» дало 178 срабатываний за 3.2 суток при
 * точности 0.742 против цены входа 0.699 (кромка +0.043, z ≈ +1.3) и физики
 * 0.691 (кромка +0.051). Правило «две РАЗНЫЕ семьи» сработало на последней
 * трети окна, где базовая линия «цена против страйка» и так угадывает
 * 80–91 %, и физику не побило вовсе (0.840 при 0.840) — отсюда разрез по
 * `families_agreed` ниже, а не свалка в одно число.
 *
 * # Как считается z — и как он считался НЕПРАВИЛЬНО раньше
 *
 * `FamilySummary` везёт СЧИТАННУЮ на сервере Σ p(1−p) (`sum_taker_pq`,
 * `sum_phys_pq`) — точную дисперсию под нулём «кошелёк откалиброван на цене
 * входа», а не приближение. Формула:
 *
 *     z = (угадано_срединеТЕХ_ЖЕ_строк − Σp) / sqrt(Σ p(1−p))
 *
 * Первая версия этого файла считала z иначе — стандартной ошибкой самой доли
 * угаданного (`sqrt(p̂(1−p̂)/n)`) — и это были ДВЕ РАЗНЫЕ ошибки, не одна:
 *
 *   1. НЕВЕРНАЯ ДИСПЕРСИЯ. Дисперсия суммы независимых Бернулли с разными p_i
 *      — это Σp_i(1−p_i), а не функция от среднего. Оценка через среднее,
 *      `n·p̄(1−p̄)`, — верхняя граница по Йенсену (p(1−p) вогнута), она
 *      ЗАВЫШАЕТ дисперсию и ЗАНИЖАЕТ z тем сильнее, чем шире разброс цен, а
 *      зона входа здесь широка по построению (0.10–0.90). Приёмка закрывается
 *      ЧИСЛОМ z, и занижать его — не безопасное направление ошибки: критерий,
 *      обязанный закрыться, тогда не закрывается никогда.
 *   2. РАЗЪЕХАВШИЕСЯ ЧИСЛИТЕЛЬ И ЗНАМЕНАТЕЛЬ. Старая формула сравнивала
 *      `won/settled` (по ВСЕМ сведённым) с `Σ taker_price/taker_priced` (по
 *      строкам СО СТАКАНОМ). Строка без стакана входит в `won`, но не в
 *      `Σ taker_price`, и кромка сдвигалась ровно на долю таких строк. У
 *      каждого сравнения теперь СВОЙ числитель на том же знаменателе:
 *      `won_taker_priced`/`taker_priced` против цены, `won_phys_priced`/
 *      `phys_priced` — против физики. Множества законно разные (стакана может
 *      не быть, замка может не быть — независимо), и оба знаменателя ОБЯЗАНЫ
 *      оставаться на экране.
 *
 * `accuracy()` (`won/settled`) — по-прежнему верное число для ЗАГОЛОВКА
 * («угадано X из Y»), это не изменилось. Ошибка была только в том, ЧТО с этой
 * долей сравнивали и как считали разброс, — и с прежней формулой ± 0.033 та же
 * цифра 178/0.742/0.699/+0.043 давала z ≈ +1.3 будто бы случайно совпадающий с
 * верным ответом; на строках с более широким разбросом цены две формулы
 * расходятся сильно, и именно поэтому дисперсию считает сервер, а не браузер.
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * Счётчики, из которых считаются доля угаданного и оба сравнения.
 *
 * Общая форма для сводки сервера (`FamilySummary`) и для куска, посчитанного
 * из ленты сигналов на экране (разрез по `families_agreed`, которого сервер не
 * умеет отдать без похода в storage/family.go — эта веха его не трогает).
 *
 * У ЦЕНЫ и ФИЗИКИ — СВОИ числитель, знаменатель и дисперсия, а не общие
 * `won`/`settled`: строка без стакана не входит в знаменатель цены, строка без
 * замка — в знаменатель физики, и эти множества законно расходятся.
 */
export interface EdgeCounts {
  signals: number;
  settled: number;
  won: number;
  /** По скольким строкам известна цена тейкерского входа, и их сумма. */
  takerPriced: number;
  sumTakerPrice: number;
  /** Угадано СРЕДИ ТЕХ ЖЕ строк, где известна цена — не `won` целиком. */
  wonTakerPriced: number;
  /** Σ p(1−p) по тем же строкам — точная дисперсия, а не n·p̄(1−p̄). */
  sumTakerPQ: number;
  /** По скольким строкам известна физика, и их сумма. РАЗНЫЙ знаменатель с
   *  ценой: строка без стакана не входит в первый, строка без замка — во второй. */
  physPriced: number;
  sumPPhys: number;
  /** То же, что `wonTakerPriced`, но для физики. */
  wonPhysPriced: number;
  /** То же, что `sumTakerPQ`, но для физики. */
  sumPhysPQ: number;
}

function emptyCounts(): EdgeCounts {
  return {
    signals: 0, settled: 0, won: 0,
    takerPriced: 0, sumTakerPrice: 0, wonTakerPriced: 0, sumTakerPQ: 0,
    physPriced: 0, sumPPhys: 0, wonPhysPriced: 0, sumPhysPQ: 0,
  };
}

/** Счётчики из сводки сервера — авторитетные, без ограничения по числу строк. */
export function countsFromSummary(s: FamilySummary): EdgeCounts {
  return {
    signals: s.signals, settled: s.settled, won: s.won,
    takerPriced: s.taker_priced, sumTakerPrice: num(s.sum_taker_price) ?? 0,
    wonTakerPriced: s.won_taker_priced, sumTakerPQ: num(s.sum_taker_pq) ?? 0,
    physPriced: s.phys_priced, sumPPhys: num(s.sum_p_phys) ?? 0,
    wonPhysPriced: s.won_phys_priced, sumPhysPQ: num(s.sum_phys_pq) ?? 0,
  };
}

/**
 * Счётчики по СЫРЫМ строкам ленты — тот же фильтр и та же пара (числитель,
 * дисперсия) на строку, что и в SQL сервера (`rule = ''`, затем `resolved`,
 * затем свои знаменатели у цены и физики, `p(1−p)` — на КАЖДОЙ строке отдельно,
 * а не по среднему).
 *
 * ПРИБЛИЖЁННО в одном смысле: `/family/signals` отдаёт не больше `limit`
 * строк, и на широком окне подсчёт может не увидеть хвост. Звать эту функцию
 * стоит только сверив длину полученной ленты с `summary.signals` — расхождение
 * и есть мера недосчёта, и прятать её нельзя.
 */
export function countsFromSignals(signals: readonly FamilySignal[]): EdgeCounts {
  const c = emptyCounts();
  for (const s of signals) {
    if (s.rule) continue; // погашенная правилом строка не участвует в отборе
    c.signals += 1;
    if (!s.resolved) continue;
    c.settled += 1;
    if (s.won) c.won += 1;
    const price = num(s.taker_price);
    if (price !== null) {
      c.takerPriced += 1;
      c.sumTakerPrice += price;
      c.sumTakerPQ += price * (1 - price);
      if (s.won) c.wonTakerPriced += 1;
    }
    const phys = num(s.p_phys);
    if (phys !== null) {
      c.physPriced += 1;
      c.sumPPhys += phys;
      c.sumPhysPQ += phys * (1 - phys);
      if (s.won) c.wonPhysPriced += 1;
    }
  }
  return c;
}

/** Доля угаданного по ВСЕМ сведённым. null — сведённых ещё не было. Это
 *  верное число для заголовка, но в кромку и z ниже оно НЕ подставляется —
 *  у тех свой числитель на своём знаменателе (см. шапку файла). */
export function accuracy(c: EdgeCounts): number | null {
  if (c.settled === 0) return null;
  return c.won / c.settled;
}

/** Средняя цена тейкерского входа по строкам, где она известна. */
export function priceMean(c: EdgeCounts): number | null {
  if (c.takerPriced === 0) return null;
  return c.sumTakerPrice / c.takerPriced;
}

/** Средняя вероятность физики по строкам, где замок наблюдали. */
export function physMean(c: EdgeCounts): number | null {
  if (c.physPriced === 0) return null;
  return c.sumPPhys / c.physPriced;
}

/**
 * z по ТОЧНОЙ дисперсии Пуассона-биномиального распределения:
 * z = (угадано − Σp) / sqrt(Σ p(1−p)). null — дисперсии не набралось (все p
 * вырождены в 0 или 1), и это НЕ ноль.
 */
function pqZ(won: number, sum: number, pq: number): number | null {
  if (pq <= 0) return null;
  return (won - sum) / Math.sqrt(pq);
}

/** Кромка над ценой, в долях единицы: угадано и цена — ОБЕ на знаменателе `takerPriced`. */
export function priceEdge(c: EdgeCounts): number | null {
  if (c.takerPriced === 0) return null;
  return c.wonTakerPriced / c.takerPriced - c.sumTakerPrice / c.takerPriced;
}
export function priceZ(c: EdgeCounts): number | null {
  return pqZ(c.wonTakerPriced, c.sumTakerPrice, c.sumTakerPQ);
}

/** Кромка над физикой, в долях единицы: угадано и физика — ОБЕ на знаменателе `physPriced`. */
export function physEdge(c: EdgeCounts): number | null {
  if (c.physPriced === 0) return null;
  return c.wonPhysPriced / c.physPriced - c.sumPPhys / c.physPriced;
}
export function physZ(c: EdgeCounts): number | null {
  return pqZ(c.wonPhysPriced, c.sumPPhys, c.sumPhysPQ);
}

// Показ z-оценки — из общего модуля: та же планка «зелёное только от двух
// сигм», что у cohort и decided. Реэкспорт, а не новый импорт у каждого
// читателя.
export { zTone, zWord };

/** Доля словами: «132/178». Прочерк — сведённых не было, а не «ноль угадано». */
export function hitWord(won: number, settled: number): string {
  if (settled === 0) return "—";
  return `${won}/${settled}`;
}

/** Проценты со знаком. null — считать было не из чего. */
export function pct(v: number | null): string {
  if (v === null) return "—";
  const sign = v > 0 ? "+" : v < 0 ? "−" : "";
  return `${sign}${Math.abs(v * 100).toFixed(1)} %`;
}

/**
 * Приговор одной группе строк (общей сводке либо одному из разрезов
 * `families_agreed`) — единая фраза с ОБОИХ сравнений и их знаменателей.
 *
 * Знаменатели названы ПОРОЗНЬ и это не многословие: строка без стакана не
 * входит в цену, строка без замка — в физику, и эти два множества законно
 * расходятся. Кромка над ценой без кромки над физикой означает, что куплена
 * сторона, которая и так выигрывала, — то же различение, что у cohort/decided.
 */
export function verdict(c: EdgeCounts): string {
  if (c.signals === 0) return "срабатываний ещё не было";
  if (c.settled === 0) {
    return `${c.signals} зачётных, исходов ещё нет`;
  }
  const parts = [
    `${c.settled} сведённых, угадано ${hitWord(c.won, c.settled)} (${pct(accuracy(c))})`,
  ];
  parts.push(
    c.takerPriced === 0
      ? "цену входа не наблюдали ни разу"
      : `против цены ${pct(priceEdge(c))} — ${zWord(priceZ(c))} на ${c.takerPriced}`,
  );
  parts.push(
    c.physPriced === 0
      ? "физику не наблюдали ни разу"
      : `против физики ${pct(physEdge(c))} — ${zWord(physZ(c))} на ${c.physPriced}`,
  );
  return parts.join("; ");
}

/**
 * Разрез ленты по числу СОШЕДШИХСЯ семей — «одна» против «две и больше».
 *
 * Не украшение, а разные архетипы с разной планкой: замер вне выборки дал
 * 0.742 на 56 срабатываниях в сутки при консенсусе одной семьи и 0.840 на 8 в
 * сутки при консенсусе двух — но у вторых кромка над физикой ровно нулевая
 * (0.840 против 0.840), то есть срабатывание позднее и ничего не сообщает
 * сверх известного. Слить их в одно число значило бы спрятать это различие.
 */
export type AgreedGroup = "one" | "many";

export function agreedGroup(familiesAgreed: number): AgreedGroup {
  return familiesAgreed <= 1 ? "one" : "many";
}

export function agreedLabel(group: AgreedGroup): string {
  return group === "one" ? "одна семья" : "две семьи и больше";
}

/** Счётчики, разложенные по `families_agreed` — см. countsFromSignals. */
export function splitByAgreement(signals: readonly FamilySignal[]): Record<AgreedGroup, EdgeCounts> {
  return {
    one: countsFromSignals(signals.filter((s) => agreedGroup(s.families_agreed) === "one")),
    many: countsFromSignals(signals.filter((s) => agreedGroup(s.families_agreed) === "many")),
  };
}

/**
 * Машинное имя правила отбора семьи в пул — словами.
 *
 * Список закрытый и повторяет `trader/family.Rule*` (00028/pool.go). Порядок
 * ветвлений там значим (рыхлость проверяется раньше значимости), но здесь
 * порядок ролей не важен — это просто словарь. Незнакомое имя отдаётся КАК
 * ЕСТЬ, а не прячется: новая причина, показанная прочерком, выглядела бы как
 * отсутствие причины.
 */
export function reasonWord(reason: string): string {
  switch (reason) {
    case "loose":
      return "рыхлая компонента: рёбер намного меньше пар — похоже на страту, а не на оператора";
    case "thin":
      return "окон меньше порога: значимости не из чего взяться";
    case "idle":
      return "давно не торгует: оператор ушёл или сменил кошельки";
    case "no_variance":
      return "дисперсии не набралось: остаток нечем перевести в запас";
    case "weak":
      return "запас над физикой ниже порога";
    default:
      return reason;
  }
}

/** Состав семьи словами: «3 кошелька». */
export function walletsWord(f: Family): string {
  const n = f.members.length;
  return `${n} ${plural(n, "кошелёк", "кошелька", "кошельков")}`;
}

/**
 * Смесь отборов словами: сколько РАЗНЫХ составов пула сложено в сводку окна.
 *
 * Состав пула неизменяем внутри ревизии, но сама ревизия меняется чаще, чем
 * набирается счёт приёмки: на боевой базе за трое суток их было одиннадцать, и
 * суточное окно панели складывало строки четырёх разных пулов. Складывать их
 * можно — но назвать смесь смесью обязательно, иначе одно число выглядит одной
 * выборкой.
 *
 * Пусто, когда ревизия в окне одна: говорить там не о чем, а лишняя строка
 * приучает глаз пролистывать предупреждение.
 */
export function revMixWord(revisions: FamilyRevRows[]): string {
  if (revisions.length < 2) return "";
  const n = revisions.length;
  const parts = revisions.map((r) => `${r.rev} (${r.signals})`).join(", ");
  return `${n} ${plural(n, "ревизия", "ревизии", "ревизий")} состава в окне: ${parts} — это ${
    n === 2 ? "два разных отбора" : `${n} разных отборов`
  }, а не одна выборка`;
}

/**
 * Подпись среза: что именно посчитано в сводке.
 *
 * Ноль — «все ревизии», и это честное умолчание: молча резать по последней
 * означало бы менять знаменатель самой сводки при каждой пересборке пула.
 */
export function revCutWord(rev: number): string {
  return rev > 0 ? `только ревизия ${rev}` : "все ревизии окна";
}

/** Остаток над физикой семьи, числом. null — дисперсии не набралось, и это НЕ ноль. */
export function residZ(f: Family): number | null {
  return num(f.resid_z);
}

/** Статус семьи в пуле: «в пуле» либо причина отказа словами. */
export function poolWord(f: Family): string {
  return f.in_pool ? "в пуле" : reasonWord(f.reason ?? "");
}

export function poolTone(f: Family): string | undefined {
  return f.in_pool ? "var(--color-good)" : "var(--color-muted)";
}

/** Плотность рёбер и доли (agree, size_ratio, q) — все в диапазоне [0, 1]. */
export function share(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `${Math.round(n * 100)} %`;
}

/**
 * Хвост Пуассона после поправки Бенджамини—Хохберга — почти всегда крошечный,
 * и округление до одного знака процента стёрло бы его до «0.0 %».
 */
export function qWord(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  if (n < 0.001) return "< 0.1 %";
  return `${(n * 100).toFixed(1)} %`;
}

/** Сколько совпадений дал бы нуль — дробное число, не доля. */
export function expectedWord(v: string): string {
  const n = num(v);
  if (n === null) return "—";
  return n.toFixed(2);
}

/** Чем связаны два кошелька — словами. */
export function linkSourceWord(source: FamilyLinkSource): string {
  return source === "second" ? "секунда входа" : "имя профиля";
}

/**
 * Улики связи, лежащие ЦЕЛИКОМ внутри одного состава.
 *
 * `/family/pool` отдаёт улики по адресам ВСЕЙ ревизии одним списком (иначе на
 * каждую строку пришлось бы делать отдельный запрос), и без этого фильтра
 * разворачивание одной семьи показало бы рёбра чужих компонент.
 */
export function linksWithin(members: readonly string[], links: readonly FamilyLink[]): FamilyLink[] {
  const set = new Set(members);
  return links.filter((l) => set.has(l.addr_a) && set.has(l.addr_b));
}

/** Деньги без копеек: перевес меряется тысячами, и центы в нём — шум. */
export function usd(v: string | null | undefined): string {
  const n = num(v);
  if (n === null) return "—";
  return `$${Math.round(n).toLocaleString("ru-RU")}`;
}

/**
 * Доля крупнейшего кошелька в обороте семьи. КАНАРЕЙКА строки: устойчиво
 * больше половины означает, что меряется один кит, а не семья.
 */
export function soloShare(v: string | null | undefined): string {
  return share(v);
}

export function soloTone(v: string | null | undefined): string | undefined {
  const n = num(v);
  if (n === null) return undefined;
  return n > 0.5 ? "var(--color-bad)" : undefined;
}

/** Фора до конца окна словами. Отрицательная законна: рынок живёт и после конца. */
export function lead(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 0) return `${Math.abs(s)} с ПОСЛЕ конца`;
  if (s < 60) return `${s} с`;
  return `${Math.floor(s / 60)} мин ${s % 60} с`;
}

/**
 * Рынок строки: актив и ДЛИТЕЛЬНОСТЬ окна, а не ширина метрики резолва.
 *
 * `window_s` — ширина метрики резолва (60 у всех серий сразу с 2026-08-13),
 * а не длительность окна (пять или пятнадцать минут). Показав первое, лента
 * рисовала бы «BTC 60s» и там и там.
 */
export function marketWord(s: FamilySignal): string {
  const parts = s.slug ? parseSeriesSlug(s.slug) : null;
  if (!parts) return s.asset || "—";
  return `${parts.asset} ${parts.duration}`;
}

/** Строка ленты: чем она кончилась. Погашенная правилом — не проигрыш. */
export function outcomeWord(s: FamilySignal): string {
  if (s.rule) return `вне зачёта: ${s.rule}`;
  if (!s.settled_at) return "ждём исхода";
  if (!s.resolved) return "исход не подтверждён";
  return s.won ? "угадано" : "мимо";
}

/** Тон строки ленты. Погашенные — приглушённые, а не красные: это не отказ. */
export function rowTone(s: FamilySignal): string | undefined {
  if (s.rule) return "var(--color-muted)";
  if (!s.resolved) return undefined;
  return s.won ? "var(--color-good)" : "var(--color-bad)";
}
